import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, Eye, EyeOff } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandMark } from '@/components/brand/BrandMark';

const MARKETING_URL = (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://praxara.io';

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [step, setStep] = useState<'credentials' | 'mfa' | 'mustSetupMfa'>('credentials');
  const [email, setEmail] = useState(() => localStorage.getItem('praxara.lastEmail') ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('praxara.rememberMe') === '1');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // rememberMe: when true, the API issues a 30-day refresh token (vs 7-day default).
      // We also remember the email locally so Sanjeev/John demo logins prefill.
      if (rememberMe) {
        localStorage.setItem('praxara.rememberMe', '1');
        localStorage.setItem('praxara.lastEmail', email);
      } else {
        localStorage.removeItem('praxara.rememberMe');
        localStorage.removeItem('praxara.lastEmail');
      }
      const res = await api.post('/api/auth/login', { email, password, rememberMe });
      if (res.data.requiresMfa) {
        setMfaToken(res.data.mfaToken);
        setStep(res.data.mustSetupMfa ? 'mustSetupMfa' : 'mfa');
        return;
      }
      setSession(res.data);
      toast.success('Signed in', `Welcome back, ${res.data.user.firstName}`);
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onMfa(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post('/api/auth/mfa/challenge', { mfaToken, code: mfaCode });
      setSession(res.data);
      toast.success('Signed in', `Welcome back, ${res.data.user.firstName}`);
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* Left panel — marketing */}
      <aside className="relative hidden overflow-hidden bg-primary p-10 text-primary-foreground lg:col-span-2 lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 opacity-[0.08]" style={{ background: 'radial-gradient(600px at 30% 20%, white, transparent 60%)' }} />
        <div className="relative flex items-center gap-2">
          <BrandMark variant="light" />
        </div>

        <div className="relative">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            GxP-ready · Part 11 e-signature controls · EU AI Act-aligned
          </div>
          <h1 className="font-display text-4xl leading-tight lg:text-5xl">
            GxP-ready AI for pharmacovigilance, regulatory, and compliance.
          </h1>
          <p className="mt-5 max-w-lg text-primary-foreground/80">
            Three modules on one tamper-evident audit chain. Every AI output cited, every approval e-signed, every record SHA-256 chained. Built so your QPPV, RA director, and Head of QA can defend it to MHRA.
          </p>

          <div className="mt-10 grid gap-3 max-w-md">
            <Feature title="Immutable audit chain" description="SHA-256 chained logs, on-demand integrity verification." />
            <Feature title="Electronic signatures" description="Password + reason per 21 CFR Section 11.10 on every approval." />
            <Feature title="Multi-provider AI routing" description="Claude, Gemini, GPT — task-routed with automatic fallback." />
            <Feature title="Azure Document Intelligence" description="Tables, scanned PDFs, structured sections — all extracted." />
          </div>
        </div>

        <div className="relative text-xs text-primary-foreground/60">
          (c) {new Date().getFullYear()} Praxara Ltd · Confidential
        </div>
      </aside>

      {/* Right panel — auth */}
      <div className="flex items-center justify-center p-8 lg:col-span-3">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <BrandMark variant="dark" />
          </div>

          {step === 'credentials' && (
            <form onSubmit={onCredentials} className="animate-fade-in space-y-5">
              <div>
                <h2 className="font-display text-2xl text-foreground">Sign in</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Welcome back. Enter your tenant credentials below.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <a href="/forgot-password" className="text-xs text-primary hover:underline">
                    Forgot?
                  </a>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Remember me on this device for 30 days
              </label>

              {error && <ErrorBanner>{error}</ErrorBanner>}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Signing in...' : 'Sign in'}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                By signing in you agree to the{' '}
                <a href={`${MARKETING_URL}/legal/terms`} target="_blank" rel="noreferrer" className="underline">
                  terms of use
                </a>{' '}
                and{' '}
                <a href={`${MARKETING_URL}/legal/esignature`} target="_blank" rel="noreferrer" className="underline">
                  e-signature agreement
                </a>{' '}
                per 21 CFR Part 11.
              </p>
            </form>
          )}

          {step === 'mfa' && (
            <form onSubmit={onMfa} className="animate-fade-in space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-display text-xl">Two-factor verification</h2>
                  <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Authentication code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={10}
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                  className="text-center text-lg font-mono tracking-widest"
                />
                <p className="text-xs text-muted-foreground">
                  Lost access? Use one of your backup codes (format: <code className="font-mono">XXXXX-XXXXX</code>).
                </p>
              </div>

              {error && <ErrorBanner>{error}</ErrorBanner>}

              <Button type="submit" className="w-full" disabled={submitting || mfaCode.length < 6}>
                {submitting ? 'Verifying...' : 'Verify and continue'}
              </Button>

              <button
                type="button"
                onClick={() => { setStep('credentials'); setMfaCode(''); setError(null); }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                · Back to email + password
              </button>
            </form>
          )}

          {step === 'mustSetupMfa' && (
            <div className="animate-fade-in space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-display text-xl">Two-factor required</h2>
                  <p className="text-sm text-muted-foreground">Your organisation requires MFA before accessing the system.</p>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/40 p-4 text-sm">
                Please contact your administrator to generate a temporary access code, or set up MFA from your account on first sign-in.
              </div>
              <Button variant="outline" onClick={() => setStep('credentials')} className="w-full">
                Back to sign-in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Feature({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
        <ShieldCheck className="h-3 w-3" />
      </div>
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-primary-foreground/70">{description}</div>
      </div>
    </div>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {children}
    </div>
  );
}
